import type { ColumnOption } from '@savvy-kit/templates/data-table-filter';

import { NOTIFICATION_ROUTING_SECTIONS } from './notification-preferences-catalog';

// CSV-curated set: only alert types the audit marks Inbox=Yes appear in the
// v2 Inbox. "Optional" (default off) and "TBD" rows are excluded by design.
// Originating sheet: "Notifications Audit — Inbox vs Activity".
//
// ponytail: this is the curated allow-list; it is the production decision
// per the audit, not a stimulus. Promote to a server-side classification
// once Rails owns the canonical rule.
export const INBOX_ALERT_TYPES = [
  'outstanding_client_task',
  'subtask_due_approaching',
  'overdue_advisor_task',
  'task_comment_mention',
  // Task-routing-managed types (comment/status). They must be in this allow-list so the
  // `alerts` query's alert_types filter doesn't strip them before the server's TaskRouting
  // runs — TaskRouting then decides inbox vs activity per the advisor's [event, relationship]
  // preference. Omitting them silently dropped every comment/status alert from the web inbox
  // regardless of the advisor's routing preference (mobile, which sends no alert_types filter,
  // showed them correctly).
  'task_comment_created',
  'task_status_changed',
  'advisor_requirements_needed',
  'subtask_assigned',
  // `task_reassigned` is the alert_type behind both "Task assigned to user"
  // (Inbox=Yes per CSV) and "Task reassigned" (Inbox=No). The producer
  // (CreateTaskReassignmentNotification) fires on any saved_change_to_assignee_id,
  // so the two CSV rows collapse to one alert_type in code — including it.
  'task_reassigned',
  'service_calendar_setup',
  'service_calendar_due_date',
  'tax_filing_ready',
  'federal_return_received',
  'federal_return_accepted',
  'federal_return_rejected',
  'state_return_received',
  'state_return_accepted',
  'state_return_rejected',
  'transfer_in',
  'rmds',
  'allocation_variance',
  'quarterly_report_failed',
  // Onboarding / Service Calendar / Portfolio / Engagement gaps (plan: 2026-06-25-notifications-inbox-alert-gaps)
  'client_uploaded_requested_file',
  'client_submitted_risk_questionnaire',
  'onboarding_complete',
  'service_calendar_task_assigned',
  'client_completed_tax_enrollment',
  'client_portal_message_received',
  'cash_variance',
  // birthdays / client_engagement / client_logins default to Activity (UNO-897) — the sheet
  // routes them off the Inbox. They stay catalog rows, so a user can still opt them into Inbox.
  // meeting_prep_ready / meeting_recap_ready are catalog types (Meetings → "Meeting prep" /
  // "Meeting recap", UNO-1049): kept in this allow-list so v2-off advisors still receive them,
  // while useInboxAlertTypes folds them out for v2-on advisors and re-adds them only when routed
  // to inbox.
  'meeting_prep_ready',
  'meeting_recap_ready',
  // Inbox only. Rails records no activity row for mention alerts, so listing this as an
  // activity event key would add a filter option that never matches.
  'household_comment_mention',
  // Inbox only, for both of the reasons above at once: no activity row for mentions, and a
  // professional contact resolves no household to key the feed on.
  'center_of_influence_rep_comment_mention',
  // Inbox only. The advisor activity feed is household-keyed on both ends and this alert
  // resolves no household, so it has no activity event key to pair with.
  'meeting_transcript_needs_household',
  'contribution_received',
] as const;

// Hand-curated inbox types the routing catalog does NOT own: task-routing
// managed types (comment/status), mandatory Canvas docs, and Inbox-only
// mentions / unattached transcripts. Every catalog type an advisor can route to
// their Inbox is folded in on top by buildInboxAlertTypeCategories, so the
// exported taxonomy covers everything that can land in the Inbox.
const INBOX_ALERT_TYPE_CATEGORIES_BASE: Readonly<Record<string, readonly string[]>> = {
  tasks: [
    'outstanding_client_task',
    'subtask_due_approaching',
    'overdue_advisor_task',
    'task_comment_mention',
    'task_comment_created',
    'task_status_changed',
    'advisor_requirements_needed',
    'subtask_assigned',
    'task_reassigned',
  ],
  service_calendar: ['service_calendar_setup', 'service_calendar_due_date', 'service_calendar_task_assigned'],
  tax: [
    'tax_filing_ready',
    'federal_return_received',
    'federal_return_accepted',
    'federal_return_rejected',
    'state_return_received',
    'state_return_accepted',
    'state_return_rejected',
    'client_completed_tax_enrollment',
  ],
  portfolio_and_accounts: ['transfer_in', 'rmds', 'allocation_variance', 'cash_variance', 'contribution_received'],
  reports: ['quarterly_report_failed'],
  client_onboarding: ['client_uploaded_requested_file', 'client_submitted_risk_questionnaire', 'onboarding_complete'],
  client_engagement: ['client_portal_message_received'],
  meetings_and_calls: ['meeting_prep_ready', 'meeting_recap_ready', 'meeting_transcript_needs_household'],
  households: ['household_comment_mention'],
  // Its own category rather than joining `households`: a professional contact is advisor-owned
  // and deliberately household-less, so filing it under Households would mislabel the chip.
  professional_contacts: ['center_of_influence_rep_comment_mention'],
};

// The routing catalog groups alert types under its own section categories; map
// those onto the Type-filter taxonomy so a folded-in catalog type lands under
// the right chip. A section whose category isn't mapped keeps its own key.
const CATALOG_CATEGORY_TO_INBOX_CATEGORY: Readonly<Record<string, string>> = {
  'more-task-activity': 'tasks',
  'client-onboarding': 'client_onboarding',
  'service-calendar': 'service_calendar',
  'leads-prospects': 'leads_and_prospects',
  tax: 'tax',
  'portfolio-accounts': 'portfolio_and_accounts',
  reports: 'reports',
  meetings: 'meetings_and_calls',
  'client-engagement': 'client_engagement',
};

// Groups every type reachable by the Inbox into the category taxonomy the
// Activity tab already uses (mirrors AdvisorActivities::SourceMapping's
// `category`), so the Inbox "Type" filter offers one chip per category and every
// inbox alert type is reachable. It starts from the hand-curated base above and
// folds in every routing-catalog type an advisor can route to their Inbox
// (`feedOnly` rows are Activity-only) — without this, opting a catalog type into
// the Inbox surfaced it in the feed but left it out of the "by type" filter
// (UNO-926). `constants.test.ts` asserts the two sources are covered exactly.
function buildInboxAlertTypeCategories(): Readonly<Record<string, readonly string[]>> {
  const result: Record<string, string[]> = {};
  const placed = new Set<string>();

  function add(category: string, type: string): void {
    if (placed.has(type)) return;
    placed.add(type);
    (result[category] ??= []).push(type);
  }

  for (const [category, types] of Object.entries(INBOX_ALERT_TYPE_CATEGORIES_BASE)) {
    for (const type of types) add(category, type);
  }
  for (const section of NOTIFICATION_ROUTING_SECTIONS) {
    const category = CATALOG_CATEGORY_TO_INBOX_CATEGORY[section.category] ?? section.category;
    for (const row of section.rows) {
      if (row.feedOnly === true) continue;
      for (const alertType of row.alertTypes) add(category, alertType);
    }
  }
  return result;
}

export const INBOX_ALERT_TYPE_CATEGORIES: Readonly<Record<string, readonly string[]>> = buildInboxAlertTypeCategories();

// Saved views and URL state persist the selected inbox filter values. State
// saved before the chips became category-keyed holds legacy Action Center
// alias keys (`ServiceCalendar`, `TaxAlerts`) — map those to their category so
// an old saved view keeps returning results instead of expanding to [].
const LEGACY_INBOX_FILTER_KEY_TO_CATEGORY: Readonly<Record<string, string>> = {
  ServiceCalendar: 'service_calendar',
  TaxAlerts: 'tax',
};

// Expands the inbox filter selection (category keys) to the concrete `type_of`
// values the `alerts` query matches on. Tolerates two kinds of legacy persisted
// values: alias keys (mapped above) and concrete alert types (passed through,
// since they are already valid query values).
export function inboxCategoriesToAlertTypes(values: readonly string[]): string[] {
  return values.flatMap((value) => {
    const category = LEGACY_INBOX_FILTER_KEY_TO_CATEGORY[value] ?? value;
    const types = INBOX_ALERT_TYPE_CATEGORIES[category];
    return types !== undefined ? [...types] : [value];
  });
}

// CSV-curated set for the Activity tab. Mirrors every row marked Activity=Yes
// in the audit. Items the audit marks Activity=No (task assigned — due in X
// days, upcoming meeting reminder, DCA reminder) and the deferred L&P bucket
// (lead_or_prospect_events_tbd) are excluded. Defensive against new
// event_keys leaking in from the resolver before they're audited.
export const ACTIVITY_EVENT_KEYS: ReadonlySet<string> = new Set([
  'outstanding_client_task',
  'overdue_advisor_task',
  'task_comment_mention',
  'task_pending_advisor',
  'subtask_assigned',
  'subtask_due_approaching',
  'client_completed_task',
  'task_created',
  'task_status_changed',
  'task_comment_created',
  'task_due_date_changed',
  'task_reassigned',
  'intake_invite_sent',
  'intake_invite_reminder_sent',
  'onboarding_invite_sent',
  'onboarding_invite_reminder_sent',
  'account_setup_confirmation_sent',
  'file_upload_request_sent',
  'client_other_request_sent',
  'risk_questionnaire_sent',
  'pre_review_check_in_sent',
  'client_uploaded_requested_file',
  'client_submitted_risk_questionnaire',
  'onboarding_complete',
  'service_calendar_invite_sent',
  'service_calendar_booking_reminder_sent',
  'service_calendar_booking_changed',
  'meeting_booked_by_client',
  'meeting_canceled_or_rescheduled_by_client',
  'service_calendar_task_assigned',
  'service_calendar_setup',
  'service_calendar_due_date',
  'service_calendar_upcoming_meeting',
  'tax_filing_ready_sent_to_client',
  'tax_filing_reminder_to_client',
  'tax_filing_late_or_missed_deadline',
  'federal_return_received',
  'federal_return_accepted',
  'federal_return_rejected',
  'state_return_received',
  'state_return_accepted',
  'state_return_rejected',
  'tax_filing_ready_advisor_alert',
  'client_completed_tax_enrollment',
  'transfer_in_detected',
  'rmds_due',
  'cash_variance',
  'allocation_variance',
  'account_opened_or_funded',
  'account_linked_or_aggregation_added',
  'large_withdrawal_detected',
  'beneficiary_or_ownership_change',
  'quarterly_report_generated',
  'quarterly_report_failed',
  'client_birthday',
  'client_under_engaged',
  'client_portal_login_activity',
  'client_portal_message_received',
  'stagnant_prospect',
  'email_from_lead',
  'sms_from_lead',
  'outbound_missed_call_reminder',
  'call_step_required',
  'inbound_missed_call',
  'new_lead_campaign_started',
  'hubspot_lead_created',
  'dialpad_call_transcript',
  'zoom_meeting_transcript',
]);

// Groups every ACTIVITY_EVENT_KEYS member under the category taxonomy
// (mirrors AdvisorActivities::SourceMapping's `category` on the Rails side), so
// the Activity "Type" filter can render a category parent with its concrete
// event-key children beneath it. `constants.test.ts` asserts these cover
// ACTIVITY_EVENT_KEYS exactly — a new event key with no category fails the test
// rather than silently dropping out of the filter.
export const ACTIVITY_EVENT_KEY_CATEGORIES: Readonly<Record<string, readonly string[]>> = {
  tasks: [
    'outstanding_client_task',
    'overdue_advisor_task',
    'task_comment_mention',
    'task_pending_advisor',
    'subtask_assigned',
    'subtask_due_approaching',
    'client_completed_task',
    'task_created',
    'task_status_changed',
    'task_comment_created',
    'task_due_date_changed',
    'task_reassigned',
  ],
  client_onboarding: [
    'intake_invite_sent',
    'intake_invite_reminder_sent',
    'onboarding_invite_sent',
    'onboarding_invite_reminder_sent',
    'account_setup_confirmation_sent',
    'file_upload_request_sent',
    'client_other_request_sent',
    'risk_questionnaire_sent',
    'pre_review_check_in_sent',
    'client_uploaded_requested_file',
    'client_submitted_risk_questionnaire',
    'onboarding_complete',
  ],
  service_calendar: [
    'service_calendar_invite_sent',
    'service_calendar_booking_reminder_sent',
    'service_calendar_booking_changed',
    'meeting_booked_by_client',
    'meeting_canceled_or_rescheduled_by_client',
    'service_calendar_task_assigned',
    'service_calendar_setup',
    'service_calendar_due_date',
    'service_calendar_upcoming_meeting',
  ],
  meetings_and_calls: ['dialpad_call_transcript', 'zoom_meeting_transcript'],
  tax: [
    'tax_filing_ready_sent_to_client',
    'tax_filing_reminder_to_client',
    'tax_filing_late_or_missed_deadline',
    'federal_return_received',
    'federal_return_accepted',
    'federal_return_rejected',
    'state_return_received',
    'state_return_accepted',
    'state_return_rejected',
    'tax_filing_ready_advisor_alert',
    'client_completed_tax_enrollment',
  ],
  portfolio_and_accounts: [
    'transfer_in_detected',
    'rmds_due',
    'cash_variance',
    'allocation_variance',
    'account_opened_or_funded',
    'account_linked_or_aggregation_added',
    'large_withdrawal_detected',
    'beneficiary_or_ownership_change',
  ],
  reports: ['quarterly_report_generated', 'quarterly_report_failed'],
  client_engagement: [
    'client_birthday',
    'client_under_engaged',
    'client_portal_login_activity',
    'client_portal_message_received',
  ],
  leads_and_prospects: [
    'stagnant_prospect',
    'email_from_lead',
    'sms_from_lead',
    'outbound_missed_call_reminder',
    'call_step_required',
    'inbound_missed_call',
    'new_lead_campaign_started',
    'hubspot_lead_created',
  ],
};

// Expands an Activity filter selection to concrete `event_key` values for the
// `advisorActivities` query. Category keys expand to their event keys; concrete
// event keys pass through (so a legacy saved view holding either shape keeps
// returning results). Mirrors `inboxCategoriesToAlertTypes`.
export function activityCategoriesToEventKeys(values: readonly string[]): string[] {
  const seen = new Set<string>();
  for (const value of values) {
    const eventKeys = ACTIVITY_EVENT_KEY_CATEGORIES[value];
    if (eventKeys !== undefined) {
      for (const key of eventKeys) seen.add(key);
    } else {
      seen.add(value);
    }
  }
  return [...seen];
}

// Human labels for the granular subtypes in the two-level Type filter. Covers
// both Inbox alert `type_of` and Activity `event_key` values — they share keys
// where they mean the same thing. Unlisted keys fall back to a humanized form
// of the raw key, so a new type is readable before anyone curates a label.
const NOTIFICATION_TYPE_LABEL_OVERRIDES: Readonly<Record<string, string>> = {
  outstanding_client_task: 'Client task outstanding',
  subtask_due_approaching: 'Subtask due soon',
  overdue_advisor_task: 'Task overdue',
  task_comment_mention: 'Mention',
  household_comment_mention: 'Mention',
  center_of_influence_rep_comment_mention: 'Mention',
  advisor_requirements_needed: 'Advisor input needed',
  subtask_assigned: 'Task assigned',
  task_reassigned: 'Task reassigned',
  task_pending_advisor: 'Pending advisor',
  client_completed_task: 'Client completed a task',
  task_status_changed: 'Status changed',
  task_comment_created: 'New comment',
  account_setup_confirmation_sent: 'Account setup',
  file_upload_request_sent: 'File requested',
  client_uploaded_requested_file: 'Client uploaded a file',
  client_submitted_risk_questionnaire: 'Risk questionnaire submitted',
  meeting_canceled_or_rescheduled_by_client: 'Meeting rescheduled',
  service_calendar_task_assigned: 'Service task assigned',
  tax_filing_ready_advisor_alert: 'Tax filing ready',
  transfer_in: 'Transfer in',
  transfer_in_detected: 'Transfer in',
  rmds: 'RMDs due',
  rmds_due: 'RMDs due',
  cash_variance: 'Cash allocation',
  allocation_variance: 'Allocation variance',
  quarterly_report_failed: 'Report failed',
  quarterly_report_generated: 'Report generated',
  client_under_engaged: 'Client under-engaged',
  client_engagement: 'Client under-engaged',
  client_portal_login_activity: 'Client login',
  client_logins: 'Client login',
  client_portal_message_received: 'Client message',
  client_birthday: 'Birthday',
  birthdays: 'Birthday',
  outbound_missed_call_reminder: 'Outbound call reminder',
  sms_from_lead: 'SMS from lead',
  email_from_lead: 'Email from lead',
  hubspot_lead_created: 'HubSpot lead created',
  dialpad_call_transcript: 'Dialpad call transcript',
  zoom_meeting_transcript: 'Zoom meeting transcript',
  meeting_prep_ready: 'Meeting prep',
  meeting_recap_ready: 'Meeting recap',
  meeting_transcript_needs_household: 'Unattached transcript',
};

export function notificationTypeLabel(key: string): string {
  const override = NOTIFICATION_TYPE_LABEL_OVERRIDES[key];
  if (override !== undefined) return override;
  const spaced = key.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export const ACTIVITY_CATEGORY_LABELS: Readonly<Record<string, string>> = {
  tasks: 'Tasks',
  client_onboarding: 'Client onboarding',
  service_calendar: 'Service calendar',
  meetings_and_calls: 'Meetings & calls',
  tax: 'Tax',
  portfolio_and_accounts: 'Portfolio & accounts',
  reports: 'Reports',
  client_engagement: 'Client engagement',
  leads_and_prospects: 'Leads & prospects',
  households: 'Households',
  professional_contacts: 'Professional contacts',
};

// Flatten a category→subtype taxonomy into grouped options for the kit's
// drill-in Type filter: each subtype carries its category as `group`, so the
// panel lists categories first and drills into their subtypes (with a
// group-level "Select all"). The live selection holds only leaf subtype values;
// the query expanders (inboxCategoriesToAlertTypes / activityCategoriesToEventKeys)
// still expand any category key a legacy saved view might carry.
// `present`, when given, restricts options to types the advisor actually has
// (from the availableInboxAlertTypes / availableActivityEventKeys facets): a
// child is kept only if present, and a category drops entirely once it has no
// kept children. Omitting `present` yields the full taxonomy (used until the
// facet query resolves, and by the household Audit log, which has no facet).
export function buildGroupedOptions(
  categoryToChildren: Readonly<Record<string, readonly string[]>>,
  order: readonly string[],
  present?: ReadonlySet<string>,
): ColumnOption[] {
  const options: ColumnOption[] = [];
  for (const category of order) {
    const children = categoryToChildren[category];
    if (children === undefined || children.length === 0) continue;
    const keptChildren = present === undefined ? children : children.filter((child) => present.has(child));
    if (keptChildren.length === 0) continue;
    const group = { value: category, label: ACTIVITY_CATEGORY_LABELS[category] ?? category };
    for (const child of keptChildren) {
      options.push({ value: child, label: notificationTypeLabel(child), group });
    }
  }
  return options;
}
